import { SpacingValues } from "@/lib/utils/SpacingValues";
import { gql, useQuery } from "@apollo/client";
import { Spacing } from "@stenajs-webui/core";
import { ErrorScreen, LoadingScreen } from "@stenajs-webui/panels";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useLocation, useParams } from "react-router";
import { routeFactory } from "@/RouteFactory";
import { AgreementDocumentsContent } from "../../agreement-documents/components/AgreementDocumentsContent";
import { RebateAgreementDocumentsContent } from "../../rebate-agreement-documents/components/RebateAgreementDocumentsContent";
import { DocumentsHeader } from "./DocumentsHeader";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { CustomerInfoPairBox } from "@/features/product-and-price/info-boxes/customer/CustomerInfoPairBox";
import { Card, CardHeader } from "@stenajs-webui/elements";
import { AgreementDocumentsTabMenu } from "./AgreementDocumentsTabMenu";

const query = gql`
  query DocumentsScreen($customerNo: ID!) {
    productPrice {
      customers {
        byId(id: $customerNo) {
          id
          name
          breadCrumbName
          custIndex
          custNo
          isParent
          preselectedHeadAgreement {
            id
          }
          custStatus {
            id
            name
          }
          parent {
            id
            preselectedHeadAgreement {
              id
            }
          }
          childCustomers {
            id
          }
          statisticalGrouping {
            id
          }
          invoiceCurrency {
            id
            code
          }
          seller {
            id
            name
          }
          salesRegion {
            id
            name
          }
          hasRebateAgreements
          writeHistory {
            createdAt {
              isoString
            }
            createdBy {
              name
            }
            modifiedAt {
              isoString
            }
            modifiedBy {
              name
            }
          }
          agreementDocuments {
            id
            agreementId
            customerNumber
            fileName
            agreementValidFromDate
            agreementValidToDate
            agreementDocumentType
            negotiationYear
            writeHistory {
              createdAt {
                isoString
              }
              createdBy {
                id
                name
              }
            }
          }
          rebateAgreementDocuments {
            id
            rebateAgreementId
            customerNumber
            fileName
            rebateAgreementValidFromDate
            rebateAgreementValidToDate
            negotiationYear
            writeHistory {
              createdAt {
                isoString
              }
              createdBy {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const DocumentsScreen: React.FC = () => {
  const { customerId: customerNo } = useParams() as { customerId: string };
  const location = useLocation();

  const rebateDocumentsPath =
    routeFactory.productAndPrice.headAgreement.rebateAgreementDocuments({
      customerNo,
    });
  const isRebateTab = location.pathname === rebateDocumentsPath;

  const { data, error, loading } = useQuery(query, {
    fetchPolicy: "cache-and-network",
    variables: { customerNo },
  });

  const customer = data?.productPrice?.customers?.byId;

  if (loading && !customer) {
    return (
      <Spacing num={SpacingValues.TWENTYFOUR}>
        <LoadingScreen />
      </Spacing>
    );
  }

  if (error) {
    return (
      <Spacing num={SpacingValues.TWENTYFOUR}>
        <ErrorScreen text={error.message} />
      </Spacing>
    );
  }

  if (!customerNo) {
    return (
      <Spacing num={SpacingValues.TWENTYFOUR}>
        <ErrorScreen text={"Missing customerId."} />
      </Spacing>
    );
  }

  return (
    <>
      <Helmet title="Documents - Customer - FPP" />
      <DocumentsHeader customer={customer} />
      <PageLayout>
        {customer && <CustomerInfoPairBox customer={customer} />}
        <Card>
          <CardHeader
            contentLeft={
              <AgreementDocumentsTabMenu
                customerNo={customer?.id ?? customerNo}
              />
            }
          />
          {isRebateTab ? (
            <RebateAgreementDocumentsContent
              rebateDocuments={customer?.rebateAgreementDocuments}
            />
          ) : (
            <AgreementDocumentsContent
              agreementDocuments={customer?.agreementDocuments}
            />
          )}
        </Card>
      </PageLayout>
    </>
  );
};
